<script setup>
import { AnimatePresence, Motion } from 'motion-v'
import { storeToRefs } from 'pinia'
import { useToastStore } from '@/stores/toast.js'
import { usePrefersReducedMotion } from '@/composables/useBreakpoint.js'
import AppIcon from './AppIcon.vue'

/** Renders the toast queue. Mounted once, in App.vue. */
const store = useToastStore()
const { toasts } = storeToRefs(store)
const reducedMotion = usePrefersReducedMotion()

const ICONS = {
  success: 'check-circle',
  error: 'x-circle',
  warning: 'alert-triangle',
  info: 'info',
}
</script>

<template>
  <div class="toast-container" role="status" aria-live="polite">
    <AnimatePresence>
      <Motion
        v-for="toast in toasts"
        :key="toast.id"
        as="div"
        class="toast"
        :class="`toast--${toast.type}`"
        :initial="reducedMotion ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.97 }"
        :animate="{ opacity: 1, y: 0, scale: 1 }"
        :exit="reducedMotion ? { opacity: 0 } : { opacity: 0, x: 24, scale: 0.97 }"
        :transition="{ duration: reducedMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }"
        layout
      >
        <div class="toast__icon">
          <AppIcon :name="ICONS[toast.type]" :size="18" />
        </div>

        <div class="toast__content">
          <p class="toast__title">{{ toast.title }}</p>
          <p v-if="toast.message" class="toast__message">{{ toast.message }}</p>
        </div>

        <button class="toast__close" aria-label="Dismiss" @click="store.dismiss(toast.id)">
          <AppIcon name="x" :size="15" />
        </button>
      </Motion>
    </AnimatePresence>
  </div>
</template>

<style scoped lang="scss">
.toast {
  position: relative;
  overflow: hidden;
}
</style>
